import React, { useMemo } from 'react';
import { Icon } from './Icon';
import type { DraftSession } from '../App';

const INDUSTRY_LABELS: Record<string, string> = {
  automotive:            'Automotive',
  real_estate:           'Real Estate',
  saas:                  'SaaS',
  professional_services: 'Professional Services',
  healthcare:            'Healthcare',
  financial:             'Financial Services',
};

const CHANNEL_LABELS: Record<string, string> = {
  email: 'Email',
  text:  'Text / SMS',
  call:  'Phone',
};

const DEAL_STATES = ['Discovery', 'Pitch', 'Stalled', 'Negotiation', 'Closing'];

const STATE_CHIP: Record<string, string> = {
  Discovery:   'chip-discovery',
  Pitch:       'chip-pitch',
  Stalled:     'chip-stalled',
  Negotiation: 'chip-negotiation',
  Closing:     'chip-closing',
};

interface UserProfile {
  industry?: string;
  role?: string;
  communication_channels?: string[];
  sales_persona?: string;
}

interface StylePageProps {
  userId: string;
  userProfile: UserProfile | null;
  sessions: DraftSession[];
}

export const StylePage: React.FC<StylePageProps> = ({ userProfile, sessions }) => {
  const isEmpty = sessions.length === 0;

  const stats = useMemo(() => {
    const toneCounts: Record<string, number> = {};
    const lengthCounts: Record<string, number> = {};
    const goalCounts: Record<string, number> = {};
    const platformCounts: Record<string, number> = {};
    const stateResponded: Record<string, { total: number; responded: number }> = {};

    DEAL_STATES.forEach(s => { stateResponded[s] = { total: 0, responded: 0 }; });

    for (const s of sessions) {
      if (s.platform) platformCounts[s.platform] = (platformCounts[s.platform] ?? 0) + 1;

      if (s.deal_state) {
        const entry = stateResponded[s.deal_state] ?? { total: 0, responded: 0 };
        entry.total++;
        if (s.lead_responded) entry.responded++;
        stateResponded[s.deal_state] = entry;
      }

      for (const dh of s.draft_history ?? []) {
        const o = dh.options_used ?? {};
        if (o.tone)   toneCounts[o.tone]   = (toneCounts[o.tone]   ?? 0) + 1;
        if (o.length) lengthCounts[o.length] = (lengthCounts[o.length] ?? 0) + 1;
        if (o.goal)   goalCounts[o.goal]   = (goalCounts[o.goal]   ?? 0) + 1;
      }
    }

    const topTone = Object.entries(toneCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
    const topLength = Object.entries(lengthCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
    const topGoal = Object.entries(goalCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
    const topPlatform = Object.entries(platformCounts).sort((a, b) => b[1] - a[1])[0]?.[0];

    const winRates = DEAL_STATES.map(state => {
      const e = stateResponded[state];
      return { state, rate: e.total > 0 ? e.responded / e.total : null, total: e.total };
    }).filter(r => r.total > 0);

    const bestState = winRates.sort((a, b) => (b.rate ?? 0) - (a.rate ?? 0))[0];

    return { toneCounts, lengthCounts, goalCounts, platformCounts, topTone, topLength, topGoal, topPlatform, winRates, bestState };
  }, [sessions]);

  const industryLabel = INDUSTRY_LABELS[userProfile?.industry ?? ''] ?? userProfile?.industry ?? null;
  const channels = userProfile?.communication_channels ?? [];
  const persona = userProfile?.sales_persona;
  const role = userProfile?.role;

  const identityLine = [persona ?? stats.topTone, role, industryLabel].filter(Boolean).join(' · ');

  return (
    <main className="main-content">
      <div className="main-container">

        {/* Hero */}
        <div className="style-hero">
          <div>
            <h2 className="style-title">Your Style</h2>
            <p className="style-sub">
              {identityLine || 'How you sell, built from your extension sessions.'}
            </p>
          </div>
          {channels.length > 0 && (
            <div className="style-channels">
              {channels.map(c => (
                <span key={c} className="style-channel-badge">
                  <Icon name="send" size={11} /> {CHANNEL_LABELS[c] ?? c}
                </span>
              ))}
            </div>
          )}
        </div>

        {isEmpty ? (
          <div className="style-empty">
            {/* Show what we know from profile even with no sessions */}
            {(industryLabel || role || channels.length > 0) && (
              <div className="style-profile-preview">
                <h3>Your profile</h3>
                <div className="style-preview-grid">
                  {industryLabel && (
                    <div className="style-preview-card">
                      <span className="style-preview-label">Industry</span>
                      <span className="style-preview-value">{industryLabel}</span>
                    </div>
                  )}
                  {role && (
                    <div className="style-preview-card">
                      <span className="style-preview-label">Role</span>
                      <span className="style-preview-value">{role}</span>
                    </div>
                  )}
                  {channels.length > 0 && (
                    <div className="style-preview-card">
                      <span className="style-preview-label">Channels</span>
                      <span className="style-preview-value">{channels.map(c => CHANNEL_LABELS[c] ?? c).join(', ')}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="style-empty-prompt">
              <Icon name="sparkle" size={24} />
              <p>Analyze leads with your extension to build your style profile.</p>
              <p className="style-empty-hint">Your tone preferences, win rates by deal stage, and top platforms will appear here automatically.</p>
            </div>
          </div>
        ) : (
          <>
            {/* Voice */}
            {Object.keys(stats.toneCounts).length > 0 && (
              <div className="style-section">
                <h3 className="style-section-heading">Your voice</h3>
                <div className="style-voice-grid">
                  {Object.entries(stats.toneCounts)
                    .sort((a, b) => b[1] - a[1])
                    .map(([tone, count]) => {
                      const total = Object.values(stats.toneCounts).reduce((s, n) => s + n, 0);
                      const pct = Math.round((count / total) * 100);
                      const isTop = tone === stats.topTone;
                      return (
                        <div key={tone} className={`style-voice-card ${isTop ? 'style-voice-card--top' : ''}`}>
                          <div className="style-voice-tone">{tone}</div>
                          <div className="style-voice-bar-wrap">
                            <div className="style-voice-bar" style={{ width: `${pct}%` }} />
                          </div>
                          <div className="style-voice-pct">{pct}%</div>
                          {isTop && <span className="style-top-badge">Primary</span>}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Win rates by deal state */}
            {stats.winRates.length > 0 && (
              <div className="style-section">
                <h3 className="style-section-heading">Where you win</h3>
                {stats.bestState && (
                  <p className="style-insight">
                    Your strongest stage is <span className={`doc-badge ${STATE_CHIP[stats.bestState.state]}`}>{stats.bestState.state}</span> with a {Math.round((stats.bestState.rate ?? 0) * 100)}% response rate.
                  </p>
                )}
                <div className="style-win-list">
                  {stats.winRates.map(({ state, rate, total }) => (
                    <div key={state} className="style-win-row">
                      <span className={`doc-badge ${STATE_CHIP[state]}`} style={{ width: 100, textAlign: 'center' }}>{state}</span>
                      <div className="style-win-track">
                        <div className={`style-win-fill ${STATE_CHIP[state]}`} style={{ width: `${Math.round((rate ?? 0) * 100)}%` }} />
                      </div>
                      <span className="style-win-label">{Math.round((rate ?? 0) * 100)}%</span>
                      <span className="style-win-count">{total} session{total !== 1 ? 's' : ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Preferences */}
            <div className="style-section">
              <h3 className="style-section-heading">How you prefer to sell</h3>
              <div className="style-prefs-grid">
                {stats.topLength && (
                  <div className="style-pref-card">
                    <span className="style-pref-label">Draft length</span>
                    <span className="style-pref-value">{stats.topLength}</span>
                  </div>
                )}
                {stats.topGoal && (
                  <div className="style-pref-card">
                    <span className="style-pref-label">Primary goal</span>
                    <span className="style-pref-value">{stats.topGoal}</span>
                  </div>
                )}
                {stats.topPlatform && (
                  <div className="style-pref-card">
                    <span className="style-pref-label">Top platform</span>
                    <span className="style-pref-value" style={{ textTransform: 'capitalize' }}>{stats.topPlatform}</span>
                  </div>
                )}
                {channels.length > 0 && (
                  <div className="style-pref-card">
                    <span className="style-pref-label">Channels</span>
                    <span className="style-pref-value">{channels.map(c => CHANNEL_LABELS[c] ?? c).join(', ')}</span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </main>
  );
};
